import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Folder, Calendar, CircleDollarSign, Users, LayoutGrid,
    Send, PenSquare, Bell, ScrollText, Zap, MessageSquare, Settings, Map as MapIcon,
} from 'lucide-react';
import { useLanguage } from '../i18n';
import SettingsModal from './modals/SettingsModal';
import DocumentsModal from './modals/DocumentsModal';
import { UploadManagerProvider } from '../contexts/UploadManagerContext';
import { useTheme } from '../hooks/useTheme';

/**
 * The launcher — the post-login entry point.
 *
 * Twelve tiles in a 4×3 grid (eleven features + the #534 PAI Map teaser, last), vertically centred over the wallpaper, with no
 * sidebar. Settings is deliberately NOT a tile: it is the circular gear pinned
 * to the bottom-left corner.
 *
 * `to` is a router path rather than a panel name because the launcher lives
 * OUTSIDE the shell — there is no panel state to toggle from here, so each tile
 * simply navigates and lets `usePanelState` resolve the workspace on arrival.
 *
 * **A tile opens EXACTLY what it names (#356).** Every feature route carries
 * `chat=0`, so the workspace arrives holding that one occupant — picking Calendar
 * used to hand the user Calendar *and* the Main Chat in a split they never asked
 * for. The Main Chat is a tile of its own for when that IS what you want.
 *
 * **The File Manager is a modal, not a slot occupant (#355).** It has no route,
 * so its tile used to navigate to a path nothing rendered and the catch-all route
 * dropped the user in the Main Chat. It now opens the overlay in place — the same
 * thing the sidebar entry does, over whatever is behind it — so neither entry
 * point forces a view change.
 *
 * The two tiles are two features since #437: "Notes & Task List" (/notes) and
 * "Reminders & Notifications" (/reminders) — the sidebar split the same way, so
 * the launcher and the sidebar now agree one-to-one.
 */
export const TILES = [
    { key: 'documents', Icon: Folder, label: 'File Manager', modal: 'documents' },
    { key: 'calendar', Icon: Calendar, label: 'Calendar', to: '/calendar?chat=0' },
    { key: 'expenses', Icon: CircleDollarSign, label: 'Finance', to: '/expenses?chat=0' },
    { key: 'agenda', Icon: Users, label: 'Contacts', to: '/agenda?chat=0' },
    { key: 'boards', Icon: LayoutGrid, label: 'Boards', to: '/boards?chat=0' },
    { key: 'telegram', Icon: Send, label: 'Telegram', to: '/telegram?chat=0' },
    { key: 'notes', Icon: PenSquare, label: 'Notes & Task List', to: '/notes?chat=0', i18nKey: 'launcherNotes' },
    { key: 'reminders', Icon: Bell, label: 'Reminders & Notifications', to: '/reminders?chat=0', i18nKey: 'launcherReminders' },
    { key: 'rulebook', Icon: ScrollText, label: 'Rulebook', to: '/rulebook?chat=0&view=expanded' },
    { key: 'energy', Icon: Zap, label: 'Energy Tank', to: '/energy?chat=0' },
    { key: 'mainChat', Icon: MessageSquare, label: 'Main Chat', to: '/' },
];

/**
 * #534 — the PAI Map TEASER: always the LAST tile (a new feature goes into
 * TILES above, i.e. before it). Not a feature yet, so it is NOT interactive:
 * a plain <div> (no button, no tabindex, no click handler), normal arrow
 * cursor, no hover/press styling — only a slow, faint border glow. Content
 * (PO, 2026-11-02): map icon, "PAI Map" (same in both languages), and
 * "In development" / "En desarrollo". Never "Coming soon", never a date, no
 * hint line (so nothing to configure server-side). Launch day: replace it with
 * a real TILES entry in the same place.
 */
const TEASER_TILE_CLASS = 'min-h-[calc((100cqw_-_16px)/2/1.42)] md:min-h-[calc((100cqw_-_78px)/4/1.42)] '
    + 'min-w-0 py-3 md:py-2 lg:py-3 rounded-modal border border-white/[0.14] bg-white/[0.03] backdrop-blur-tile '
    + 'flex flex-col items-center justify-center gap-2 lg:gap-3 cursor-default select-none animate-teaser-glow';

export default function LauncherPage({ onLogout }) {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { theme, toggleTheme } = useTheme();
    const [settingsOpen, setSettingsOpen] = React.useState(false);
    const [documentsOpen, setDocumentsOpen] = React.useState(false);

    return (
        <div className="relative h-dvh overflow-hidden bg-pai-void text-white">
            <div
                aria-hidden="true"
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: "url('/wallpaper.jpg')" }}
            />
            <div aria-hidden="true" className="absolute inset-0 bg-[rgba(5,7,10,0.64)]" />

            <div className="relative h-full overflow-auto" data-testid="launcher">
                {/* #478 — bottom padding below `lg` clears the fixed gear
                    (34px offset + 60px button + 16px air), so the last row can
                    always be scrolled out from under it. */}
                <div className="min-h-full box-border px-6 pt-10 pb-[110px] md:px-[72px] md:pt-16 lg:pb-16 flex flex-col justify-center">
                    {/* #478 — the tile height used to come from `aspect-[1.42]`
                        alone. A box's height then only grows to fit its content
                        through the aspect-ratio "automatic minimum size", which
                        WebKit (every iOS browser) does not apply to these
                        <button> grid items — so a two-line label on a portrait
                        phone overflowed both borders. The 1.42 shape is now a
                        MIN-height in container-query units (`cqw` = this grid's
                        width; the calc is exactly the old aspect height per
                        column count), so a tile is never shorter than before
                        and grows with its content in every engine;
                        `auto-rows-fr` keeps every row as tall as the tallest. */}
                    <div className="[container-type:inline-size] grid grid-cols-2 md:grid-cols-4 auto-rows-fr gap-4 md:gap-[26px]">
                        {TILES.map(({ key, Icon, label, to, modal, i18nKey }) => (
                            <button
                                key={key}
                                data-testid={`launcher-tile-${key}`}
                                onClick={() => (modal ? setDocumentsOpen(true) : navigate(to))}
                                className="min-h-[calc((100cqw_-_16px)/2/1.42)] md:min-h-[calc((100cqw_-_78px)/4/1.42)]
                                           min-w-0 py-3 md:py-2 lg:py-3
                                           rounded-modal border border-white/[0.14] bg-white/[0.03] backdrop-blur-tile
                                           flex flex-col items-center justify-center gap-3 lg:gap-5
                                           transition-colors duration-150
                                           hover:bg-white/[0.09] hover:border-white/[0.34]
                                           focus:outline-none focus-visible:border-white/80 focus-visible:shadow-glow-sm"
                            >
                                <Icon size={36} strokeWidth={1.75} className="shrink-0" />
                                {/* Phones/tablets: the label scales with the
                                    viewport so its longest word fits a 320px
                                    phone's tile; desktop (lg+) keeps 20px. */}
                                <span className="font-bold text-[clamp(14px,4.6vw,20px)] md:text-[clamp(15px,1.9vw,20px)] lg:text-[20px]
                                                 leading-[1.2] lg:leading-normal tracking-[-0.01em] text-center px-2
                                                 max-w-full [overflow-wrap:anywhere]">
                                    {t(i18nKey || key, label)}
                                </span>
                            </button>
                        ))}
                        <div
                            data-testid="launcher-tile-paiMap"
                            data-teaser="true"
                            className={TEASER_TILE_CLASS}
                        >
                            <MapIcon size={36} strokeWidth={1.75} className="shrink-0" aria-hidden="true" />
                            <span className="font-bold text-[clamp(14px,4.6vw,20px)] md:text-[clamp(15px,1.9vw,20px)] lg:text-[20px]
                                             leading-[1.2] lg:leading-normal tracking-[-0.01em] text-center px-2
                                             max-w-full [overflow-wrap:anywhere]">
                                PAI Map
                            </span>
                            <span
                                data-testid="launcher-paimap-status"
                                className="text-[12px] md:text-[13px] font-medium uppercase tracking-[0.08em] text-pai-meta text-center px-2"
                            >
                                {t('launcherPaiMapStatus', 'In development')}
                            </span>
                        </div>
                    </div>
                    {/* Extra terms and 18 (#410): the legal footer was removed
                        from the launcher — a signed-in user is already past
                        every gate that needs it (the five unauthenticated
                        screens, the two onboarding gates, and User Settings →
                        Legal all still carry it). */}
                </div>
            </div>

            {/* Settings — a control, not a feature, so it never joins the grid. */}
            <button
                data-testid="launcher-settings"
                onClick={() => setSettingsOpen(true)}
                title={t('systemSettings', 'System Settings')}
                aria-label={t('systemSettings', 'System Settings')}
                className="group fixed left-[34px] bottom-[34px] w-[60px] h-[60px] rounded-full
                           border border-white/[0.16] bg-white/[0.05] backdrop-blur-tile
                           flex items-center justify-center
                           transition-[transform,background-color,border-color] duration-[350ms] ease-out
                           hover:bg-white/[0.12] hover:border-white/[0.36] hover:rotate-90"
            >
                <Settings size={26} className="opacity-85" />
            </button>

            {/* #355 — the File Manager overlay, opened in place by its tile. It is
                the SAME modal the sidebar entry opens inside the workspace; neither
                entry point navigates, so neither changes what is behind it. */}
            {/* The File Manager's upload affordance calls `useUploadManager()`,
                which THROWS outside a provider — the launcher lives outside the
                shell, so it brings its own. The two never coexist (different
                routes), so there is no shared-state question to answer. */}
            <UploadManagerProvider>
                <DocumentsModal
                    isOpen={documentsOpen}
                    onClose={() => setDocumentsOpen(false)}
                />
            </UploadManagerProvider>

            <SettingsModal
                isOpen={settingsOpen}
                mode="system"
                onClose={() => setSettingsOpen(false)}
                theme={theme}
                onToggleTheme={toggleTheme}
                onLogout={onLogout}
                onOpenEnergyPanel={() => { setSettingsOpen(false); navigate('/energy?chat=0'); }}
            />
        </div>
    );
}
